import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'

export default function SettingsTeam() {
  const { user, role } = useAuth()
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState(null)

  const isAdmin = role === 'admin'

  useEffect(() => { fetchMembers() }, [])

  async function fetchMembers() {
    setLoading(true)
    // Recupero utenti con ruolo
    const { data } = await supabase
      .from('user_roles')
      .select('*, user:user_id(id, email, raw_user_meta_data)')
    setMembers(data ?? [])
    setLoading(false)
  }

  async function changeRole(userId, newRole) {
    await supabase.from('user_roles').update({ role: newRole }).eq('user_id', userId)
    showToast('✅ Ruolo aggiornato')
    fetchMembers()
  }

  async function makeAdmin(userId) {
    if (!isAdmin) return
    await changeRole(userId, 'admin')
  }

  function showToast(msg) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  return (
    <div style={s.root}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Team</h1>
          <p style={s.sub}>Gestisci l'accesso dei membri del team</p>
        </div>
      </div>

      <div style={s.infoBox}>
        <div style={s.infoIcon}>ℹ</div>
        <div style={s.infoText}>
          <strong>Come aggiungere un membro:</strong> la persona deve accedere con il suo account <code>@gallicantus.it</code> — apparirà automaticamente in questa lista al primo login.
          Solo gli admin possono cambiare i ruoli.
        </div>
      </div>

      <div style={s.table}>
        <div style={s.thead}>
          <div style={{...s.th, flex: 2}}>Utente</div>
          <div style={{...s.th, flex: 1}}>Email</div>
          <div style={{...s.th, width: 100}}>Ruolo</div>
          {isAdmin && <div style={{...s.th, width: 120}}>Azioni</div>}
        </div>

        {loading ? (
          <div style={s.loading}>Caricamento...</div>
        ) : members.length === 0 ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>◎</div>
            <div>Nessun membro ancora. Attendi che i colleghi facciano il primo login.</div>
          </div>
        ) : (
          members.map(m => {
            const meta = m.user?.raw_user_meta_data ?? {}
            const email = m.user?.email ?? '—'
            const name = meta.full_name ?? meta.name ?? email.split('@')[0]
            const avatar = meta.avatar_url
            const isMe = m.user_id === user?.id

            return (
              <div key={m.id} style={s.row}>
                <div style={{...s.td, flex: 2, gap: 10}}>
                  {avatar
                    ? <img src={avatar} alt="" style={s.avatar} referrerPolicy="no-referrer" />
                    : <div style={s.avatarFallback}>{name.slice(0, 2).toUpperCase()}</div>
                  }
                  <div>
                    <div style={s.name}>{name} {isMe && <span style={s.meBadge}>tu</span>}</div>
                  </div>
                </div>
                <div style={{...s.td, flex: 1}}>
                  <span style={s.email}>{email}</span>
                </div>
                <div style={{...s.td, width: 100}}>
                  <span style={{...s.roleBadge, ...(m.role === 'admin' ? s.roleAdmin : s.roleMember)}}>
                    {m.role}
                  </span>
                </div>
                {isAdmin && (
                  <div style={{...s.td, width: 120}}>
                    {!isMe && (
                      <select
                        style={s.roleSelect}
                        value={m.role}
                        onChange={e => changeRole(m.user_id, e.target.value)}
                      >
                        <option value="member">member</option>
                        <option value="admin">admin</option>
                      </select>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {toast && <div style={s.toast}>{toast}</div>}
    </div>
  )
}

const s = {
  root: { padding: 28, flex: 1, overflowY: 'auto', fontFamily: 'Geist, sans-serif' },
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 600, color: '#e8e8f0', fontFamily: 'DM Serif Display, serif', margin: 0 },
  sub: { fontSize: 12, color: '#55556a', fontFamily: 'monospace', marginTop: 4 },
  infoBox: { display: 'flex', gap: 12, background: 'rgba(107,92,231,0.1)', border: '1px solid rgba(107,92,231,0.2)', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: 12, color: '#8888a8', lineHeight: 1.6 },
  infoIcon: { color: '#8b7ef5', flexShrink: 0, fontSize: 16 },
  infoText: { flex: 1 },
  table: { background: '#111118', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'flex', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  th: { fontSize: 10, color: '#55556a', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'monospace' },
  row: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  td: { display: 'flex', alignItems: 'center', flexShrink: 0 },
  avatar: { width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' },
  avatarFallback: { width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#6b5ce7,#a78bfa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white', flexShrink: 0 },
  name: { fontSize: 13, fontWeight: 500, color: '#e8e8f0', display: 'flex', alignItems: 'center', gap: 6 },
  meBadge: { fontSize: 9, background: 'rgba(107,92,231,0.2)', color: '#8b7ef5', borderRadius: 4, padding: '1px 5px', fontFamily: 'monospace' },
  email: { fontSize: 11, color: '#8888a8', fontFamily: 'monospace' },
  roleBadge: { fontSize: 10, padding: '3px 8px', borderRadius: 10, fontFamily: 'monospace' },
  roleAdmin: { background: 'rgba(107,92,231,0.2)', color: '#8b7ef5' },
  roleMember: { background: 'rgba(255,255,255,0.05)', color: '#55556a' },
  roleSelect: { background: '#17171f', border: '1px solid rgba(255,255,255,0.12)', color: '#e8e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontFamily: 'monospace', outline: 'none', cursor: 'pointer' },
  loading: { color: '#55556a', fontSize: 13, padding: 40, textAlign: 'center' },
  empty: { padding: 40, textAlign: 'center', color: '#55556a', fontSize: 13, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 },
  emptyIcon: { fontSize: 28 },
  toast: { position: 'fixed', bottom: 24, right: 24, background: '#17171f', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '12px 18px', fontSize: 13, color: '#e8e8f0', zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' },
}
